// src/pages/ClinicalDataCollector/components/output/HypertensionSection.jsx

import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useSelector } from 'react-redux';

// 👇 nuevo: importamos la data simulada
import hypertensionData from '../data/hypertensionSectionData.json';

const iconMap = {
  exercise: <FitnessCenterIcon />,
  diet: <LocalHospitalIcon />,
  weight: <ShowChartIcon />,
  triplepill: <TimelineIcon />,
  meds: <FavoriteIcon />
};

const HypertensionSection = () => {
  const { PatientSelect } = useSelector((state) => state.patientMapSelect || {});
  const patients = hypertensionData?.patients || [];

  const selectedPatient = useMemo(() => {
    if (!patients.length) return null;

    if (PatientSelect?.memberId) {
      const base =
        (PatientSelect.memberId.length || 1) +
        (PatientSelect.age || 0) * 13;
      const idx = Math.abs(base) % patients.length;
      return patients[idx];
    }

    const randomIdx = Math.floor(Math.random() * patients.length);
    return patients[randomIdx];
  }, [PatientSelect, patients]);

  if (!selectedPatient) return null;

  const {
    name,
    profile,
    bpCategories = [],
    nntStats = [],
    recommendations = [],
    sharedDecisionPoints = []
  } = selectedPatient;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Hypertension Section"
        // subheader={`Simulated profile: ${name} — ${profile}`}
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<FavoriteIcon fontSize="large" color="error" />}
      />
      <Divider />
      <CardContent>
        {/* BP Categories */}
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <TimelineIcon sx={{ mr: 1 }} /> BP Reading Distribution
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="center"># of Readings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bpCategories.map((row, i) => (
              <TableRow key={i} hover>
                <TableCell>{row.category}</TableCell>
                <TableCell align="center">{row.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* NNT Statistics */}
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ShowChartIcon sx={{ mr: 1 }} /> NNT for Prevention
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Outcome</TableCell>
              <TableCell align="center">NNT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nntStats.map((stat, i) => (
              <TableRow key={i} hover>
                <TableCell>{stat.outcome}</TableCell>
                <TableCell align="center">{stat.nnt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Recommendations */}
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LocalHospitalIcon sx={{ mr: 1 }} /> Key Recommendations
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {recommendations.map((rec, i) => (
            <ListItem key={i} disablePadding>
              <ListItemIcon>
                {iconMap[rec.iconType] || <LocalHospitalIcon />}
              </ListItemIcon>
              <ListItemText primary={rec.text} />
            </ListItem>
          ))}
        </List>

        {/* Shared Decision Making */}
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FavoriteIcon sx={{ mr: 1 }} /> Shared Decision-Making Points
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          {sharedDecisionPoints.map((point, i) => (
            <li key={i}>
              <Typography variant="body2">{point}</Typography>
            </li>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default HypertensionSection;
